import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { printProfessionalInvoice, printThermalReceipt, downloadInvoice } from '../lib/invoiceUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Search, DollarSign, Receipt, ChevronLeft, ChevronRight,
  Eye, CreditCard, Banknote, Printer, Download, Calendar, FileText,
  RotateCcw, Loader2, AlertTriangle, CheckCircle2, Minus, Plus
} from 'lucide-react';
import { fmtCurrency, fmtNumber } from '../lib/localeUtils';
import { toast } from 'sonner';
import GlAuditLink from '../components/GlAuditLink';
import { keyed, skeletonKeys } from '../lib/listKeys';

const PAYMENT_ICONS = {
  cash: Banknote,
  card: CreditCard,
  upi: CreditCard,
};

export default function SalesHistoryPage() {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [tillFilter, setTillFilter] = useState('all');
  const [tills, setTills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [viewSale, setViewSale] = useState(null);
  const [viewSaleLoading, setViewSaleLoading] = useState(false);

  // Return state
  const [returnDialog, setReturnDialog] = useState(false);
  const [returnSale, setReturnSale] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [returnReason, setReturnReason] = useState('');
  const [returnRequestId, setReturnRequestId] = useState(() => crypto.randomUUID());
  const [returning, setReturning] = useState(false);
  const [saleReturns, setSaleReturns] = useState([]);
  const [viewSaleReturns, setViewSaleReturns] = useState([]);

  const canReturn = user?.role === 'super_admin' || user?.role === 'admin' ||
    (Array.isArray(user?.effective_permissions) && user.effective_permissions.includes('sales_return')) ||
    (Array.isArray(user?.custom_permissions) && user.custom_permissions.includes('sales_return'));

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15, search };
      if (dateFrom) params.date_from = dateFrom + 'T00:00:00';
      if (dateTo) params.date_to = dateTo + 'T23:59:59';
      if (paymentFilter !== 'all') params.payment_method = paymentFilter;
      if (tillFilter !== 'all') params.terminal_id = tillFilter;
      const res = await api.get('/sales', { params });
      setSales(res.data.sales || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setSummary(res.data.summary || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, dateFrom, dateTo, paymentFilter, tillFilter]);

  // Which tills exist, so a disputed receipt can be traced to a register.
  // Only users who can manage staff can read the register; everyone else just
  // sees the Till column on the rows.
  useEffect(() => {
    api.get('/terminals')
      .then(res => setTills(res.data.terminals || []))
      .catch(() => setTills([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchSales, 300);
    return () => clearTimeout(t);
  }, [fetchSales]);

  const handleDownloadCSV = () => {
    if (sales.length === 0) return;
    const headers = ['Sale ID', 'Date', 'Items', 'Subtotal', 'Discount', 'Total', 'Payment', 'Customer', 'Origin', 'Online Request Type'];
    const rows = sales.map(s => [
      s.sale_id,
      new Date(s.created_at).toLocaleString(),
      s.items?.length || 0,
      s.subtotal?.toFixed(2),
      s.discount?.toFixed(2),
      s.total?.toFixed(2),
      s.payment_method,
      s.customer_name || '-',
      s.origin === 'online' ? 'Online' : 'In-store',
      s.online_request_type || '-'
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pharmxai-sales-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openSaleDetail = async (sale) => {
    setViewSale(sale);
    setViewSaleLoading(true);
    setViewSaleReturns([]);
    try {
      // The list row doesn't carry portal QR metadata (only the single-sale
      // endpoint attaches it) — re-fetch the full sale so receipts/print
      // previews show the patient portal QR code.
      const res = await api.get(`/sales/${sale.sale_id}`);
      setViewSale(res.data);
      const retRes = await api.get(`/sales/${sale.sale_id}/returns`);
      setViewSaleReturns(retRes.data.returns || []);
    } catch (err) {
      console.error(err);
    } finally {
      setViewSaleLoading(false);
    }
  };

  const openReturnDialog = async (sale) => {
    setReturnSale(sale);
    setReturnRequestId(crypto.randomUUID());
    setReturnReason('');
    // Build from one current response, never stale React state plus a second read.
    const alreadyReturned = {};
    try {
      const retRes = await api.get(`/sales/${sale.sale_id}/returns`);
      const existingReturns = retRes.data.returns || [];
      existingReturns.forEach(r => r.items?.forEach(ri => {
        alreadyReturned[ri.product_id] = (alreadyReturned[ri.product_id] || 0) + ri.quantity;
      }));
      setSaleReturns(existingReturns);
    } catch { toast.error('Could not verify previous returns. Reconnect and try again.'); return; }

    const items = (sale.items || []).map(item => {
      const returned = alreadyReturned[item.product_id] || 0;
      const maxReturn = item.quantity - returned;
      return { ...item, already_returned: returned, max_returnable: maxReturn, return_qty: 0 };
    }).filter(i => i.max_returnable > 0);

    setReturnItems(items);
    setReturnDialog(true);
  };

  const updateReturnQty = (idx, delta) => {
    setReturnItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const newQty = Math.max(0, Math.min(item.max_returnable, item.return_qty + delta));
      return { ...item, return_qty: newQty };
    }));
  };

  // The customer only ever paid `returnSale.total` (after discounts), never
  // the undiscounted line value — prorate the preview the same way the
  // backend computes the actual refund, so the cashier isn't shown a
  // bigger number than what actually gets refunded.
  const returnTotalGross = returnItems.reduce((sum, i) => sum + (i.return_qty * i.unit_price), 0);
  const discountRatio = returnSale?.subtotal > 0 ? Math.min(1, Math.max(0, returnSale.total / returnSale.subtotal)) : 1;
  const returnTotal = returnTotalGross * discountRatio;
  const hasReturnItems = returnItems.some(i => i.return_qty > 0);

  const handleReturn = async () => {
    if (!hasReturnItems) { toast.error('Select at least one item to return'); return; }
    setReturning(true);
    try {
      const items = returnItems.filter(i => i.return_qty > 0).map(i => ({
        product_id: i.product_id, product_name: i.product_name, quantity: i.return_qty, unit_price: i.unit_price,
      }));
      const res = await api.post(`/sales/${returnSale.sale_id}/return`, { items, reason: returnReason, request_id: returnRequestId });
      const reversal = res.data?.loyalty_reversal;
      if (reversal && (reversal.points_refunded > 0 || reversal.points_clawed_back > 0)) {
        const parts = [];
        if (reversal.points_refunded > 0) parts.push(`${fmtNumber(reversal.points_refunded)} pts refunded`);
        if (reversal.points_clawed_back > 0) parts.push(`${fmtNumber(reversal.points_clawed_back)} pts clawed back`);
        toast.success(`Sale return processed. Stock restored. Loyalty: ${parts.join(', ')}.`);
      } else {
        toast.success('Sale return processed successfully. Stock has been restored.');
      }
      setReturnDialog(false);
      setViewSale(null);
      fetchSales();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Return failed');
    } finally {
      setReturning(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="sales-history-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Sales History
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{total} transactions</p>
        </div>
        <Button variant="outline" onClick={handleDownloadCSV} className="gap-1.5" disabled={sales.length === 0} data-testid="export-csv-btn">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-3 gap-4">
          <Card data-testid="summary-count">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground font-medium">Transactions</p>
              <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{summary.count}</p>
            </CardContent>
          </Card>
          <Card data-testid="summary-revenue">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground font-medium">Total Revenue</p>
              <p className="text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {fmtCurrency(summary.total_revenue)}
              </p>
            </CardContent>
          </Card>
          <Card data-testid="summary-discount">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground font-medium">Total Discounts</p>
              <p className="text-2xl font-bold mt-1 text-amber-600 dark:text-amber-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {fmtCurrency(summary.total_discount)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-3 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by sale ID or customer..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10"
              data-testid="sales-search"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="w-36 h-9 text-xs"
                data-testid="date-from"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="w-36 h-9 text-xs"
                data-testid="date-to"
              />
            </div>
            <Select value={paymentFilter} onValueChange={(v) => { setPaymentFilter(v); setPage(1); }}>
              <SelectTrigger className="w-32 h-9" data-testid="payment-filter">
                <SelectValue placeholder="Payment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
              </SelectContent>
            </Select>
            {tills.length > 0 && (
              <Select value={tillFilter} onValueChange={(v) => { setTillFilter(v); setPage(1); }}>
                <SelectTrigger className="w-36 h-9" data-testid="till-filter">
                  <SelectValue placeholder="Till" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tills</SelectItem>
                  {tills.map(t => (
                    <SelectItem key={t.terminal_id} value={t.terminal_id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sales Table */}
      <Card data-testid="sales-table-card">
        <ScrollArea className="w-full">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sale ID</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="hidden md:table-cell">Customer</TableHead>
                <TableHead className="hidden md:table-cell">Items</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead className="hidden lg:table-cell">Till</TableHead>
                <TableHead className="hidden md:table-cell">Status</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right w-[60px]">View</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                skeletonKeys(5, 'sale-skeleton').map((key) => (
                  <TableRow key={key}><TableCell colSpan={9}><div className="h-10 bg-muted rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : sales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                    <Receipt className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p>No sales found</p>
                  </TableCell>
                </TableRow>
              ) : sales.map(sale => {
                const PayIcon = PAYMENT_ICONS[sale.payment_method] || Banknote;
                return (
                  <TableRow key={sale.sale_id} className="cursor-pointer hover:bg-muted/50" onClick={() => openSaleDetail(sale)} data-testid={`sale-row-${sale.sale_id}`}>
                    <TableCell className="font-mono text-xs font-medium">{sale.sale_id}{sale.origin === 'online' && <span className="block mt-1 text-emerald-700 font-sans" data-testid={`sale-online-origin-${sale.sale_id}`}>Online · {sale.online_request_type}</span>}</TableCell>
                    <TableCell className="text-sm">
                      <div>
                        <p>{new Date(sale.created_at).toLocaleDateString()}</p>
                        <p className="text-xs text-muted-foreground">{new Date(sale.created_at).toLocaleTimeString()}</p>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{sale.customer_name || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{sale.items?.length || 0} items</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs gap-1 capitalize">
                        <PayIcon className="h-3 w-3" /> {sale.payment_method}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-muted-foreground" data-testid={`sale-till-${sale.sale_id}`}>
                      {sale.terminal_label || '—'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {sale.return_status === 'full' ? (
                        <Badge variant="destructive" className="text-[10px]">Returned</Badge>
                      ) : sale.return_status === 'partial' ? (
                        <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Partial Return</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-green-600 border-green-300">Complete</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-sm">
                      {sale.return_status ? (
                        <div>
                          <span className={sale.return_status === 'full' ? 'line-through text-muted-foreground' : ''}>{fmtCurrency(sale.total)}</span>
                          {sale.return_status === 'partial' && sale.effective_total != null && (
                            <p className="text-xs text-primary font-normal">Net: {fmtCurrency(sale.effective_total)}</p>
                          )}
                        </div>
                      ) : fmtCurrency(sale.total)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`view-sale-${sale.sale_id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollArea>
        {pages > 1 && (
          <div className="flex items-center justify-between p-3 border-t">
            <p className="text-xs text-muted-foreground">Page {page} of {pages}</p>
            <div className="flex gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Sale Detail / Invoice Dialog */}
      <Dialog open={!!viewSale} onOpenChange={() => setViewSale(null)}>
        <DialogContent className="max-w-lg" data-testid="sale-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-4 w-4" /> Invoice Details
            </DialogTitle>
          </DialogHeader>
          {viewSale && (
            <div className="space-y-4">
              {viewSaleLoading && (
                <p className="text-[10px] text-muted-foreground text-center -mb-2" data-testid="sale-detail-loading">Loading portal QR…</p>
              )}
              {/* Header */}
              <div className="text-center border-b pb-3">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>DutiLyne</h3>
                <p className="text-[10px] text-muted-foreground">AI-Powered Pharmacy Management</p>
                <Separator className="my-2" />
                <p className="text-xs font-mono">{viewSale.sale_id}</p>
                {viewSale.origin === 'online' && <p className="text-sm text-emerald-700" data-testid="sale-detail-online-origin">Online · {viewSale.online_request_type}</p>}
                <p className="text-xs text-muted-foreground">{new Date(viewSale.created_at).toLocaleString()}</p>
              </div>

              {/* Customer */}
              {(viewSale.customer_name || viewSale.customer_phone) && (
                <div className="text-xs">
                  {viewSale.customer_name && <p>Customer: <strong>{viewSale.customer_name}</strong></p>}
                  {viewSale.customer_phone && <p>Phone: {viewSale.customer_phone}</p>}
                </div>
              )}

              {/* Prescription Info */}
              {viewSale.prescription_info && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-2 text-xs">
                  <p className="font-medium text-amber-800 dark:text-amber-200">⚕️ Prescription Sale</p>
                  {viewSale.prescription_info.doctorName && <p className="text-amber-700 dark:text-amber-300">Doctor: Dr. {viewSale.prescription_info.doctorName}</p>}
                </div>
              )}

              {/* Items */}
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  <span>Item</span><span>Amount</span>
                </div>
                <Separator />
                {keyed(viewSale.items, item => item.product_name).map(({ item, key }) => (
                  <div key={key} className="flex justify-between text-sm py-1">
                    <div>
                      <p className="font-medium">{item.product_name}</p>
                      <p className="text-xs text-muted-foreground">{item.quantity} x {fmtCurrency(item.unit_price)}</p>
                    </div>
                    <p className="font-medium">{fmtCurrency((item.quantity * item.unit_price))}</p>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <Separator />
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmtCurrency(viewSale.subtotal)}</span></div>
                {viewSale.discount > 0 && (
                  <div className="flex justify-between text-destructive"><span>Discount</span><span>-{fmtCurrency(viewSale.discount)}</span></div>
                )}
                <Separator />
                <div className="flex justify-between font-bold text-lg pt-1">
                  <span>Total</span><span>{fmtCurrency(viewSale.total)}</span>
                </div>
                <p className="text-xs text-muted-foreground capitalize">Payment: {viewSale.payment_method}</p>
                <p className="text-xs text-muted-foreground" data-testid="sale-till">
                  Till: {viewSale.terminal_label || 'Browser (no terminal)'}
                </p>
                <div className="pt-1">
                  <GlAuditLink source="sale" sourceRef={viewSale.sale_id} />
                </div>
              </div>

              {viewSale.notes && (
                <p className="text-xs text-muted-foreground border-t pt-2">Note: {viewSale.notes}</p>
              )}

              {/* Print/Download Actions */}
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1 gap-1.5" 
                  onClick={() => printThermalReceipt(viewSale)}
                  data-testid="print-thermal-btn"
                >
                  <Printer className="h-4 w-4" /> Thermal
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 gap-1.5" 
                  onClick={() => printProfessionalInvoice(viewSale)}
                  data-testid="print-invoice-btn"
                >
                  <FileText className="h-4 w-4" /> Print Invoice
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 gap-1.5" 
                  onClick={() => downloadInvoice(viewSale)}
                  data-testid="download-invoice-btn"
                >
                  <Download className="h-4 w-4" /> Download
                </Button>
              </div>

              {/* Return Status */}
              {viewSale.return_status && (
                <div className={`p-3 rounded-lg border text-sm ${viewSale.return_status === 'full' ? 'bg-destructive/10 border-destructive/30' : 'bg-amber-500/10 border-amber-500/30'}`} data-testid="sale-return-status">
                  <p className="font-medium flex items-center gap-1.5">
                    <RotateCcw className="h-3.5 w-3.5" />
                    {viewSale.return_status === 'full' ? 'Fully Returned' : 'Partially Returned'}
                  </p>
                  {viewSale.total_returned > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">Returned: {fmtCurrency(viewSale.total_returned)} | Net: {fmtCurrency(viewSale.effective_total ?? viewSale.total)}</p>
                  )}
                  {(() => {
                    const totalRefunded = viewSaleReturns.reduce((sum, r) => sum + (r.loyalty_reversal?.points_refunded || 0), 0);
                    const totalClawedBack = viewSaleReturns.reduce((sum, r) => sum + (r.loyalty_reversal?.points_clawed_back || 0), 0);
                    if (totalRefunded <= 0 && totalClawedBack <= 0) return null;
                    return (
                      <p className="text-xs text-muted-foreground mt-1" data-testid="sale-return-loyalty-reversal">
                        Loyalty:{totalRefunded > 0 && ` ${fmtNumber(totalRefunded)} pts refunded`}{totalRefunded > 0 && totalClawedBack > 0 && ','}{totalClawedBack > 0 && ` ${fmtNumber(totalClawedBack)} pts clawed back`}
                      </p>
                    );
                  })()}
                </div>
              )}

              {/* Return Button */}
              {canReturn && viewSale.return_status !== 'full' && (
                <Button
                  variant="destructive"
                  className="w-full gap-2"
                  onClick={() => openReturnDialog(viewSale)}
                  data-testid="process-return-btn"
                >
                  <RotateCcw className="h-4 w-4" /> Process Return
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Return Sale Dialog */}
      <Dialog open={returnDialog} onOpenChange={(o) => { if (!o) setReturnDialog(false); }}>
        <DialogContent className="max-w-lg" data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <RotateCcw className="h-4 w-4" /> Process Sale Return
            </DialogTitle>
            <DialogDescription>
              Select items and quantities to return. Stock will be restored and revenue adjusted.
            </DialogDescription>
          </DialogHeader>

          {returnSale && (
            <div className="space-y-4">
              <div className="text-xs text-muted-foreground p-2 bg-muted rounded-lg">
                <p>Original Sale: <strong className="text-foreground">{returnSale.sale_id}</strong></p>
                <p>Date: {new Date(returnSale.created_at).toLocaleString()}</p>
                <p>Original Total: <strong className="text-foreground">{fmtCurrency(returnSale.total)}</strong></p>
              </div>

              {/* Return Items */}
              <div className="space-y-2">
                <Label>Select Items to Return</Label>
                {returnItems.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">All items have already been returned.</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {returnItems.map((item, idx) => (
                      <div key={item.product_id} className={`p-3 rounded-lg border ${item.return_qty > 0 ? 'border-destructive/50 bg-destructive/5' : 'bg-card'}`} data-testid={`return-item-${item.product_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{item.product_name}</p>
                            <p className="text-xs text-muted-foreground">
                              Sold: {item.quantity} | Max returnable: {item.max_returnable} | {fmtCurrency(item.unit_price)} each
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 ml-3">
                            <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateReturnQty(idx, -1)} disabled={item.return_qty <= 0} data-testid={`return-qty-minus-${idx}`}>
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-8 text-center text-sm font-bold" data-testid={`return-qty-${idx}`}>{item.return_qty}</span>
                            <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateReturnQty(idx, 1)} disabled={item.return_qty >= item.max_returnable} data-testid={`return-qty-plus-${idx}`}>
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        {item.return_qty > 0 && (
                          <p className="text-xs font-medium text-destructive mt-1">
                            Return: {item.return_qty} x {fmtCurrency(item.unit_price)} = {fmtCurrency(item.return_qty * item.unit_price)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reason */}
              <div className="space-y-2">
                <Label>Reason for Return</Label>
                <Textarea
                  value={returnReason}
                  onChange={e => setReturnReason(e.target.value)}
                  placeholder="e.g., Customer complaint, wrong product, expired..."
                  rows={2}
                  data-testid="return-reason"
                />
              </div>

              {/* Return Summary */}
              {hasReturnItems && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-semibold text-destructive">Refund Amount</p>
                      <p className="text-xs text-muted-foreground">{returnItems.reduce((sum, i) => sum + i.return_qty, 0)} unit(s) being returned</p>
                      {discountRatio < 1 && (
                        <p className="text-xs text-muted-foreground" data-testid="return-discount-note">after {fmtCurrency(returnTotalGross - returnTotal)} sale/loyalty discount</p>
                      )}
                    </div>
                    <p className="text-xl font-bold text-destructive" data-testid="return-total">{fmtCurrency(returnTotal)}</p>
                  </div>
                  <div className="flex items-start gap-1.5 mt-2 text-xs text-muted-foreground">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>Stock quantities will be restored. Sale revenue will be reduced by this amount.</span>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setReturnDialog(false)} disabled={returning}>Cancel</Button>
                <Button variant="destructive" onClick={handleReturn} disabled={returning || !hasReturnItems} data-testid="confirm-return-btn" className="gap-2">
                  {returning ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  {returning ? 'Processing...' : 'Confirm Return'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
